import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, PopupEvent, Theme } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DesignerTopbarPopupDataboardsComponent from 'webapp/components/designer/topbar/popup-databoards';
import FormSelectComponent from 'webapp/components/form/select';
import type { OptionRaw } from 'webapp/components/form/select';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import { ViewType } from 'webapp/models-custom/filter-sort/mixed-in-designer';

interface Args {
	disableBoardsDropdown: boolean;
	selectedBoard: Board | null;
	selectedBoardLoop: BoardLoop | null;
	onBoardChange: (board: Board) => void;
	onBoardLoopChange: (boardLoop: BoardLoop) => void;
}

export default class DesignerTopbarPagerComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;

	@tracked isDropdownActive = false;

	get fakeBoardsList() {
		const selectedBoard = this.args.selectedBoard,
			list: OptionRaw<string>[] = [];
		// only current board, bcos dropdown does not open anyways
		if (selectedBoard) {
			list.push({
				value: selectedBoard.id!,
				label: selectedBoard.name,
				label3: selectedBoard.isAccountTemplate ? 'Template' : '',
				color: selectedBoard.color,
			});
		}
		return list;
	}

	get isAddNewBoardBtnShown() {
		return !this.args.selectedBoard?.isAccountTemplate;
	}

	@action
	addNewBoard() {
		if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			this.analyticsService.event(TrackingEvent.DATABOARD_CREATED_NEW, {
				origin: 'greenplus',
			});
			this.navigationService.transitionToRoute('designer');
		}
	}

	@action
	addNewTemplate() {
		this.mmBoardsService.openAccountTemplateEditPopup(null, 'designer_board_pager');
	}

	@action
	toggleDataboardList(event: JQuery.TriggeredEvent) {
		const attachTo = (event.target as HTMLElement)?.nextSibling as HTMLElement; // targeting select.custom-select next sibling .select2

		if (!attachTo) {
			return;
		}

		const isPopupOpen = this.popupService.isOpen(DesignerTopbarPopupDataboardsComponent);

		if (!isPopupOpen) {
			const popup = this.popupService.createContext(
				DesignerTopbarPopupDataboardsComponent,
				attachTo,
				{
					theme: Theme.DARK,
					popupClass: 'popup-type-designer-topbar-databoards',
					allowStacked: true,
				},
			);

			later(
				this,
				() => {
					popup.open({
						selectedBoard: this.args.selectedBoard,
						selectedBoardLoop: this.args.selectedBoardLoop,
						onChangeSelection: (item: Board | BoardLoop) => {
							if (item instanceof BoardLoop) {
								this.args.onBoardLoopChange(item);
							} else {
								this.args.onBoardChange(item);
							}
						},
						onCreateNew: (viewType: ViewType) => {
							if (viewType === ViewType.LOOP) {
								// board loop
								this.mmBoardLoopsService.startCreateNewFlow('designer').then(
									(loopModel) => {
										this.args.onBoardLoopChange(loopModel);
									},
									() => {
										/* skip */
									},
								);
							} else if (viewType === ViewType.ACCOUNT_TEMPLATE) {
								this.addNewTemplate();
							} else {
								this.addNewBoard();
							}
						},
					});
				},
				200,
			); // Ensure that logic in Form::Select (onOpeningFn) is executed so we can show popup

			this.isDropdownActive = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isDropdownActive = false;
				}
			});
		} else {
			void this.popupService.closeAll(); // close all popups when toggling on select for the 2nd time
		}
	}

	@action
	doNothing() {
		// what the name says
	}
	<template>
		<div id="boardsPager">

			<div class="select-container {{if this.isDropdownActive 'select-container--active'}}">
				<FormSelectComponent
					name="boards"
					@selected={{@selectedBoard.id}}
					@options={{this.fakeBoardsList}}
					@isNumeric={{false}}
					@tooltipOnSelectionFromProperty="text"
					@showSelectionTooltipOnlyIfTruncated={{true}}
					@selectedTooltipWidth={{300}}
					@theme="dark"
					@size="small"
					@placeholder="Untitled"
					@isDisabled={{@disableBoardsDropdown}}
					@onOpening={{this.toggleDataboardList}}
					@onChange={{this.doNothing}}
				/>
				<VerificationBadgeComponent
					@verification={{@selectedBoard.verificationInfo}}
					@entityLabel="databoard"
					@size="sm"
				/>
			</div>

			{{#if this.isAddNewBoardBtnShown}}
				<a role="button" id="addBoardLink" class="button" {{on "click" this.addNewBoard}}>
					<CommonUiIconComponent @name="Plus" @size="md" />
					<ToolTipComponent @text="New dashboard" />
				</a>
			{{else}}
				<a
					role="button"
					id="addBoardLink"
					class="button"
					{{on "click" this.addNewTemplate}}
				>
					<CommonUiIconComponent @name="Plus" @size="md" />
					<ToolTipComponent @text="New account template" @maxWidth={{250}} />
				</a>
			{{/if}}
		</div>
	</template>
}
